/**
 * CustomColorPicker Component
 * 
 * Reusable color picker with save/cancel functionality
 * Includes click-outside-to-close behavior
 */

import React, { useState, useEffect, useRef } from 'react';
import { useColorScheme } from '../../contexts/ColorSchemeContext';

export interface CustomColorPickerProps {
  isOpen: boolean;
  onClose: () => void;
  currentColor: string;
  onChange: (color: string) => void;
  label: string;
}

const CustomColorPicker: React.FC<CustomColorPickerProps> = ({
  isOpen,
  onClose,
  currentColor,
  onChange,
  label
}) => {
  const [tempColor, setTempColor] = useState(currentColor);
  const pickerRef = useRef<HTMLDivElement>(null);
  const { getColorClasses } = useColorScheme();
  const colors = getColorClasses();

  // Update temp color when current color changes
  useEffect(() => {
    setTempColor(currentColor);
  }, [currentColor]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleSave = () => {
    onChange(tempColor);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={pickerRef}
      className={`absolute top-full left-0 mt-2 p-4 rounded-lg shadow-xl border z-50 ${colors.cardBackground} ${colors.borderColor}`}
      style={{ minWidth: '200px' }}
    >
      <h4 className={`text-sm font-semibold mb-3 ${colors.textPrimary}`}>
        {label}
      </h4>
      <div className="space-y-3">
        <div className="flex items-center space-x-3">
          <div
            className="w-12 h-12 rounded border-2 border-gray-300 dark:border-gray-600"
            style={{ backgroundColor: tempColor }}
          />
          <div className="flex-1">
            <input
              type="color"
              value={tempColor}
              onChange={(e) => setTempColor(e.target.value)}
              className="w-full h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
            />
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleSave}
            className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-all duration-300 ${colors.bgAccent} text-white hover:opacity-90`}
          >
            Save
          </button>
          <button
            onClick={onClose}
            className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-all duration-300 ${colors.textSecondary} hover:${colors.cardBackground} border border-gray-300 dark:border-gray-700`}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomColorPicker;

